/**
 * PartnerStripePayouts — iter98
 * Stripe Connect status, onboarding CTA, and payouts overview.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, ShieldCheck, AlertCircle, ArrowRight, Banknote, CalendarCheck2,
  Wallet, ExternalLink,
} from 'lucide-react';
import PartnerAccountLayout from '../../../components/PartnerAccountLayout';
import { StripeWordmark } from '../../../components/PaymentLogos';
import { API, authHeaders, fmtMoney, fmtDate } from './_utils';

function Stat({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-5" data-testid={testId}>
      <Icon className="w-4 h-4 text-white/55 mb-2" />
      <div className="text-2xl font-black text-white">{value}</div>
      <p className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 mt-1.5 font-bold">{label}</p>
      {sub && <p className="text-[11px] text-white/40 mt-1">{sub}</p>}
    </div>
  );
}

export default function PartnerStripePayouts() {
  const [loading, setLoading] = useState(true);
  const [partner, setPartner] = useState(null);
  const [overview, setOverview] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [dash, list] = await Promise.all([
          axios.get(`${API}/partners/dashboard`, { headers: authHeaders() }),
          axios.get(`${API}/partners/commissions`, { headers: authHeaders() }),
        ]);
        setPartner(dash.data.partner);
        setOverview(dash.data.overview);
        setCommissions(list.data.items || []);
      } catch {
        toast.error('Could not load Stripe status');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // After returning from Stripe, refresh status once.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get('stripe')) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/partners/stripe/status`, { headers: authHeaders() });
        setPartner(data.partner);
        if (data.partner?.stripe_details_submitted) toast.success('Stripe connected!');
      } catch {/* ignore */}
      window.history.replaceState({}, '', '/partners/account/stripe');
    })();
  }, []);

  const startStripe = async () => {
    if (starting) return;
    setStarting(true);
    try {
      const { data } = await axios.post(
        `${API}/partners/stripe/connect-onboarding`,
        {
          return_path: '/partners/account/stripe?stripe=return',
          refresh_path: '/partners/account/stripe?stripe=refresh',
        },
        { headers: authHeaders() }
      );
      if (data?.url) window.location.href = data.url;
      else { toast.error('Could not start Stripe onboarding'); setStarting(false); }
    } catch (err) {
      const d = err?.response?.data?.detail || 'Stripe error';
      toast.error(typeof d === 'string' ? d : 'Stripe error');
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <PartnerAccountLayout currentKey="stripe" title="Stripe & Payouts">
        <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-gold-300" /></div>
      </PartnerAccountLayout>
    );
  }

  const connected = !!partner?.stripe_details_submitted;
  const lastPayout = overview?.last_payout;
  const paidCommissions = commissions.filter((c) => ['paid', 'succeeded'].includes(c.status));

  return (
    <PartnerAccountLayout
      currentKey="stripe"
      title="Stripe & Payouts"
      subtitle="NoFee does not store or manage banking information — payouts run securely through Stripe Connect."
    >
      {/* Status banner */}
      <div className={`rounded-2xl border p-6 mb-8 ${connected ? 'border-emerald-500/30 bg-emerald-500/[0.05]' : 'border-amber-500/30 bg-amber-500/[0.05]'}`} data-testid="partner-stripe-status">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            {connected ? (
              <ShieldCheck className="w-6 h-6 text-emerald-300 mt-1 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-6 h-6 text-amber-300 mt-1 flex-shrink-0" />
            )}
            <div>
              <p className="text-[10.5px] uppercase tracking-[0.24em] font-bold mb-1" style={{ color: connected ? '#6ee7b7' : '#fcd34d' }}>
                Stripe Status
              </p>
              <h2 className="text-2xl font-black text-white">{connected ? 'Connected' : 'Not Connected'}</h2>
              <p className="text-sm text-white/65 mt-2 max-w-lg leading-relaxed">
                {connected
                  ? 'Your Stripe Connect account is active. Commissions are paid automatically as your member restaurants pay their monthly subscription.'
                  : 'NoFee does not store or manage banking information. All commissions are paid securely through Stripe Connect. Connect your Stripe account to automatically receive recurring commission payments.'}
              </p>
              {/* iter104 — Owner asked to surface the explicit 10% rate
                  so partners see, on the same screen as the Connect CTA,
                  exactly how much will be transferred each cycle. */}
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gold-500/[0.08] border border-gold-500/30">
                <span className="text-[10.5px] uppercase tracking-[0.22em] text-gold-400 font-bold">Commission</span>
                <span className="text-[13px] text-white/90 font-semibold">
                  NoFee transfers <span className="text-gold-200 font-extrabold">10%</span> of every member restaurant subscription directly to this account, every month, automatically.
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={startStripe}
            disabled={starting}
            className={`inline-flex items-center gap-2 h-12 px-6 rounded-xl font-bold text-sm whitespace-nowrap transition-all disabled:opacity-50 ${
              connected
                ? 'border border-white/15 text-white/80 hover:border-[#635BFF]/60 hover:text-[#a8a3ff] bg-white/[0.02]'
                : 'bg-[#635BFF] hover:bg-[#544ee6] text-white shadow-[0_12px_28px_-10px_rgba(99,91,255,0.65)]'
            }`}
            data-testid="partner-stripe-cta"
          >
            {starting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : connected ? (
              <>
                <ExternalLink className="w-4 h-4" /> Update <StripeWordmark className="h-4 w-auto" monochrome /> Account
              </>
            ) : (
              <>
                Connect <StripeWordmark className="h-4 w-auto text-white" monochrome />
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Payout stats */}
      <div className="grid sm:grid-cols-3 gap-3 mb-8">
        <Stat icon={Banknote}       label="Last Payout"           value={lastPayout ? fmtMoney(lastPayout.amount_cents) : '—'} sub={lastPayout ? fmtDate(lastPayout.created_at) : 'No payouts yet'} testId="partner-stripe-last" />
        <Stat icon={CalendarCheck2} label="Next Estimated Payout" value={fmtMoney(overview?.commission_this_month_cents)} sub={connected ? 'Paid at end of cycle' : 'Connect Stripe first'} testId="partner-stripe-next" />
        <Stat icon={Wallet}         label="Total Paid"            value={fmtMoney(overview?.commission_total_cents)} sub="Lifetime"     testId="partner-stripe-total" />
      </div>

      <h3 className="text-lg font-bold text-white mb-4">Payment History</h3>
      {paidCommissions.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/[0.08] bg-white/[0.015] p-10 text-center">
          <Banknote className="w-7 h-7 text-white/40 mx-auto mb-3" />
          <p className="text-sm font-bold text-white mb-1">No payments yet</p>
          <p className="text-xs text-white/50 max-w-[420px] mx-auto">
            Once Stripe is connected and your first member restaurant pays its subscription, payouts will appear here.
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] overflow-x-auto" data-testid="partner-stripe-history">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.06]">
                {['Date', 'Amount', 'Status'].map((h) => (
                  <th key={h} className="text-left text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-bold px-5 py-3.5 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paidCommissions.map((c) => (
                <tr key={c.id || `${c.created_at}-${c.amount_cents}`} className="border-b border-white/[0.03] last:border-0">
                  <td className="px-5 py-3.5 text-white/75 whitespace-nowrap">{fmtDate(c.created_at)}</td>
                  <td className="px-5 py-3.5 text-gold-200 font-semibold whitespace-nowrap">{fmtMoney(c.amount_cents)}</td>
                  <td className="px-5 py-3.5 whitespace-nowrap">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full border text-[10.5px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
                      {c.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PartnerAccountLayout>
  );
}
